import { ILauncherToken, ILauncherUrlCheckResult } from '../interfaces/launcher';
import { getCookie } from "../utils";
import { getApiUrl } from "../utils/apiUrl";
import { getEmbedContext } from "../utils/embedContext";

/** A failed launcher API call. `status` is 0 when the request never got a response (offline, DNS, CORS). */
export class LauncherApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }

  /** The server understood the request and refused it, so repeating it will not help. */
  get isRejected() {
    return this.status >= 400 && this.status < 500 && this.status !== 408 && this.status !== 429;
  }
}

class LauncherApiService {

  checkUrl = async (url: string): Promise<ILauncherUrlCheckResult> => {
    const referrerId = getCookie('sharer_id');
    const embedContext = getEmbedContext();
    return this.post<ILauncherUrlCheckResult>('/api/v1/Launcher/Check', { url, referrerId, embedContext });
  }

  refreshToken = async (refreshToken: string): Promise<ILauncherToken> => {
    const token = await this.post<ILauncherToken>('/api/v1/Launcher/RefreshToken', {
      refreshToken,
    });
    if (!token?.accessToken || !token?.refreshToken) {
      throw new LauncherApiError(502, 'Refresh returned no token');
    }
    return token;
  }

  private post = async <T>(path: string, body: object): Promise<T> => {
    let response: Response;
    try {
      response = await fetch(`${getApiUrl(process.env.REACT_APP_BASE_URL!)}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });
    } catch (error) {
      throw new LauncherApiError(0, String(error));
    }

    if (!response.ok) {
      throw new LauncherApiError(response.status, `${path} failed with ${response.status}`);
    }

    try {
      return await response.json();
    } catch (error) {
      throw new LauncherApiError(502, `${path} returned an unreadable body`);
    }
  }
}

export const launcherApiService = new LauncherApiService();
